"use client";

import { useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import { RESUME_UPLOAD_ACCEPT, type JobItemDto } from "@artha/api-contracts";
import { BottomSheet } from "@artha/ui";
import { CountryRestrictionModal } from "@/modules/geolocation";
import type { CandidatePersonalizationController } from "../hooks/useCandidatePersonalization";
import { useResumePersonalizationSheet } from "../hooks/useResumePersonalizationSheet";
import { ManageStep } from "./sheet-steps/ManageStep";
import { OtpStep } from "./sheet-steps/OtpStep";
import { ParsingStep } from "./sheet-steps/ParsingStep";
import { ReviewStep } from "./sheet-steps/ReviewStep";
import { SignInStep } from "./sheet-steps/SignInStep";
import { UploadStep } from "./sheet-steps/UploadStep";
import { FitResultStep } from "./sheet-steps/FitResultStep";

type ResumePersonalizationSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void | Promise<void>;
  controller: CandidatePersonalizationController;
  mode?: "personalization" | "apply_eligibility";
  job?: JobItemDto | null;
  onViewJobs?: () => void;
};

export function ResumePersonalizationSheet({
  isOpen,
  onClose,
  onComplete,
  controller,
  mode = "personalization",
  job,
  onViewJobs,
}: ResumePersonalizationSheetProps) {
  const t = useTranslations("profile.resumePersonalization");

  if (!isOpen) {
    return (
      <BottomSheet isOpen={false} onClose={onClose} title={t("title")}>
        {null}
      </BottomSheet>
    );
  }

  return (
    <OpenResumePersonalizationSheet
      key={controller.isSessionLoading ? "loading" : "ready"}
      onClose={onClose}
      onComplete={onComplete}
      controller={controller}
      mode={mode}
      job={job}
      onViewJobs={onViewJobs}
    />
  );
}

function OpenResumePersonalizationSheet({
  onClose,
  onComplete,
  controller,
  mode = "personalization",
  job,
  onViewJobs,
}: Omit<ResumePersonalizationSheetProps, "isOpen">) {
  const t = useTranslations("profile.resumePersonalization");
  const tApply = useTranslations("jobDetails.resumeEligibility");
  const isApplyEligibility = mode === "apply_eligibility";
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sheet = useResumePersonalizationSheet({
    onClose,
    onComplete,
    controller,
    initialStep: isApplyEligibility
      ? controller.isAuthenticated && controller.editableProfile
        ? "parsing"
        : "upload"
      : undefined,
    uploadSource: isApplyEligibility ? "apply_gate" : "jobs_page",
    job,
  });

  if (sheet.step === "no_match") {
    return (
      <CountryRestrictionModal
        isOpen
        title={tApply("noMatchTitle")}
        subtitle={tApply("noMatchDescription")}
        actionLabel={tApply("viewJobs")}
        onClose={onClose}
        onShowSimilarJobs={onViewJobs || onClose}
      />
    );
  }

  return (
    <BottomSheet
      isOpen
      onClose={onClose}
      title={
        sheet.step === "manage" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {t("manage.title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {t("manage.description")}
            </span>
          </div>
        ) : sheet.step === "upload" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {isApplyEligibility ? tApply("title") : t("title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {isApplyEligibility ? tApply("subtitle") : t("subtitle")}
            </span>
          </div>
        ) : sheet.step === "review" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {isApplyEligibility ? tApply("reviewTitle") : t("review.title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {isApplyEligibility
                ? tApply("reviewDescription")
                : t("review.description")}
            </span>
          </div>
        ) : sheet.step === "otp" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold text-[#344054] leading-snug">
              {isApplyEligibility ? tApply("otpTitle") : t("otp.title")}
            </span>
            <span className="block text-xs font-normal text-[#667085] leading-normal">
              {isApplyEligibility ? tApply("otpSubtitle") : t("otp.subtitle")}
            </span>
          </div>
        ) : sheet.step === "unavailable" ? (
          <div className="space-y-1 pr-2">
            <span className="block text-lg font-semibold leading-snug text-[#344054]">
              {tApply("unavailableTitle")}
            </span>
          </div>
        ) : isApplyEligibility ? (
          tApply("title")
        ) : (
          t("title")
        )
      }
      headerAction={
        sheet.showBack ? (
          <button
            type="button"
            aria-label={t("actions.goBack")}
            disabled={controller.isRequestingCode}
            className="rounded-full border border-black bg-white p-1.5 disabled:cursor-wait disabled:opacity-50"
            onClick={sheet.goBack}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : undefined
      }
      desktopPlacement="bottom"
      className="bg-[#FFF8E8] px-5 pb-7 pt-3"
      contentClassName="z-[70]"
      overlayClassName="z-[69]"
    >
      <div className="space-y-5">
        {sheet.error ? (
          <p
            role="alert"
            className="rounded-[10px] border border-[#9D1C1C] bg-[#FFF0F0] px-3 py-2 text-sm font-semibold text-[#9D1C1C]"
          >
            {sheet.error}
          </p>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept={RESUME_UPLOAD_ACCEPT}
          className="sr-only"
          aria-label={t("upload.fileInputLabel")}
          onChange={(event) => {
            sheet.chooseFile(event.target.files?.[0] || null);
            event.target.value = "";
          }}
        />

        {sheet.step === "upload" ? (
          <UploadStep
            selectedFile={sheet.selectedFile}
            isUploading={controller.isUploading}
            onChooseFile={() => inputRef.current?.click()}
            onNext={() => void sheet.upload()}
            nextLabel={
              isApplyEligibility ? tApply("checkMyMatch") : undefined
            }
            showSignIn={!controller.isAuthenticated}
            onSignIn={() => {
              sheet.setStep("sign_in");
            }}
          />
        ) : null}
        {sheet.step === "parsing" ? (
          <ParsingStep restoring={controller.isSessionLoading} />
        ) : null}
        {sheet.step === "review" ? (
          <ReviewStep
            form={sheet.reviewForm}
            isRequestingCode={controller.isRequestingCode}
            onChange={sheet.setReviewForm}
            onSubmit={() => void sheet.review()}
            submitLabel={
              isApplyEligibility ? tApply("checkMyMatch") : undefined
            }
            requestingLabel={
              isApplyEligibility ? tApply("checkingFit") : undefined
            }
          />
        ) : null}
        {sheet.step === "sign_in" ? (
          <SignInStep
            email={sheet.signInEmail}
            isRequestingCode={controller.isRequestingCode}
            onEmailChange={sheet.setSignInEmail}
            onSubmit={() => void sheet.signIn()}
          />
        ) : null}
        {sheet.step === "otp" ? (
          <OtpStep
            code={sheet.code}
            email={controller.maskedEmail || sheet.verificationEmail}
            isRequestingCode={controller.isRequestingCode}
            isVerifying={controller.isVerifying}
            onCodeChange={sheet.setCode}
            onSubmit={() => void sheet.verify()}
            onResend={() => void sheet.resend()}
          />
        ) : null}
        {sheet.step === "manage" ? (
          <ManageStep
            profile={controller.editableProfile}
            isLoggingOut={controller.isLoggingOut}
            onUpdate={sheet.showUpload}
            onLogout={() => void sheet.logout()}
          />
        ) : null}
        {sheet.step === "unavailable" ? (
          <FitResultStep
            onRetry={sheet.retryFit}
          />
        ) : null}
      </div>
    </BottomSheet>
  );
}
